import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  TREASURY_FIELD_TYPE_OPTIONS,
  TREASURY_FIELD_TYPES,
} from "../constants/treasuryFormsOptions";
import {
  changeTreasuryFieldType,
  isMovementCapableField,
  isSelectionField,
  makeOption,
} from "../utils/treasuryFormEditorModel";
import TreasuryFormIntegrationEditor from "./TreasuryFormIntegrationEditor";

const roundedInputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "16px" },
};

function FieldPreview({ field }) {
  switch (field.fieldType) {
    case TREASURY_FIELD_TYPES.LONG_TEXT:
      return (
        <TextField
          disabled
          value=""
          multiline
          minRows={2}
          placeholder="Respuesta larga..."
          sx={roundedInputSx}
          fullWidth
        />
      );
    case TREASURY_FIELD_TYPES.NUMBER:
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            disabled
            value=""
            size="small"
            placeholder="0"
            sx={roundedInputSx}
            fullWidth
          />
          <Chip
            label={field.numberFormat === "DECIMAL" ? "Decimal" : "Entero"}
            size="small"
          />
        </Stack>
      );
    case TREASURY_FIELD_TYPES.AMOUNT:
      return (
        <TextField
          disabled
          size="small"
          value="ARS $ 0,00"
          sx={roundedInputSx}
          fullWidth
        />
      );
    case TREASURY_FIELD_TYPES.IMAGES:
      return (
        <Stack spacing={0.5}>
          <Button disabled variant="outlined" startIcon={<AddPhotoAlternateOutlinedIcon />}>
            Agregar imágenes
          </Button>
          <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
            Hasta 3 imágenes JPG, JPEG o PNG. No se suben archivos desde el constructor.
          </Typography>
        </Stack>
      );
    case TREASURY_FIELD_TYPES.CONCEPT_AMOUNT_LIST:
      return (
        <Stack spacing={0.8}>
          {[1, 2].map((row) => (
            <Stack key={row} direction="row" spacing={1}>
              <TextField disabled size="small" value="Concepto" sx={roundedInputSx} fullWidth />
              <TextField
                disabled
                size="small"
                value="ARS $ Importe"
                sx={{ ...roundedInputSx, width: 150 }}
              />
            </Stack>
          ))}
          <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
            La respuesta admite hasta 20 filas.
          </Typography>
        </Stack>
      );
    case TREASURY_FIELD_TYPES.SHORT_TEXT:
      return (
        <TextField
          disabled
          value=""
          size="small"
          placeholder="Respuesta corta..."
          sx={roundedInputSx}
          fullWidth
        />
      );
    default:
      return null;
  }
}

export default function TreasuryFormFieldEditor({
  field,
  index,
  total,
  errors = {},
  accounts,
  categories,
  financialLoading,
  financialError,
  onChange,
  onDelete,
  onMove,
}) {
  const selection = isSelectionField(field.fieldType);

  const updateOption = (optionIndex, value) => {
    const options = field.options.map((option, currentIndex) =>
      currentIndex === optionIndex ? { ...option, value } : option
    );
    onChange({ ...field, options });
  };

  const deleteOption = (optionIndex) => {
    if (field.options.length <= 2) return;
    onChange({
      ...field,
      options: field.options.filter((_, currentIndex) => currentIndex !== optionIndex),
    });
  };

  return (
    <Paper
      elevation={0}
      data-testid={`field-editor-${index}`}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: "22px",
        border: "1.7px solid rgba(0,0,0,0.42)",
        bgcolor: "rgba(255,255,255,0.96)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          borderColor: "primary.500",
          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <TextField
            variant="standard"
            label={`Campo ${index + 1}`}
            placeholder="Pregunta"
            value={field.label}
            onChange={(event) => onChange({ ...field, label: event.target.value })}
            inputProps={{ maxLength: 255 }}
            error={Boolean(errors.label)}
            helperText={errors.label}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: 17,
                fontWeight: 700,
                color: "primary.900",
                py: 0.2,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "rgba(0,0,0,0.35)",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottomColor: "primary.600",
              },
            }}
            fullWidth
          />

          <TextField
            select
            size="small"
            label="Tipo de campo"
            value={field.fieldType}
            onChange={(event) =>
              onChange(changeTreasuryFieldType(field, event.target.value))
            }
            sx={{
              minWidth: { sm: 205 },
              bgcolor: "white",
              "& .MuiOutlinedInput-root": {
                height: 36,
                borderRadius: "9px",
                fontSize: 13,
                fontWeight: 700,
              },
            }}
          >
            {TREASURY_FIELD_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
            <Tooltip title="Subir campo">
              <span>
                <IconButton
                  aria-label={`Subir campo ${index + 1}`}
                  size="small"
                  disabled={index === 0}
                  onClick={() => onMove(index, index - 1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Bajar campo">
              <span>
                <IconButton
                  aria-label={`Bajar campo ${index + 1}`}
                  size="small"
                  disabled={index === total - 1}
                  onClick={() => onMove(index, index + 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar campo">
              <IconButton
                aria-label={`Eliminar campo ${index + 1}`}
                size="small"
                color="error"
                onClick={() => onDelete(field.localId)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={field.isRequired}
              onChange={(event) => onChange({ ...field, isRequired: event.target.checked })}
            />
          }
          label="Obligatorio"
          sx={{ alignSelf: "flex-end", mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
        />

        {selection ? (
          <Stack spacing={0.8}>
            {field.options.map((option, optionIndex) => (
              <Stack key={option.localId} direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{ width: 22, fontSize: 12, color: "neutral.600" }}>
                  {optionIndex + 1}.
                </Typography>
                <TextField
                  variant="standard"
                  placeholder={`Opción ${optionIndex + 1}`}
                  value={option.value}
                  onChange={(event) => updateOption(optionIndex, event.target.value)}
                  sx={{
                    maxWidth: 360,
                    "& .MuiInputBase-input": { fontSize: 12, fontWeight: 700, py: 0.2 },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "rgba(0,0,0,0.25)",
                    },
                  }}
                  fullWidth
                />
                <Tooltip title="Eliminar opción">
                  <span>
                    <IconButton
                      aria-label={`Eliminar opción ${optionIndex + 1}`}
                      size="small"
                      disabled={field.options.length <= 2}
                      onClick={() => deleteOption(optionIndex)}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onChange({ ...field, options: [...field.options, makeOption()] })}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Agregar opción
            </Button>
            {errors.options ? <Alert severity="error">{errors.options}</Alert> : null}
          </Stack>
        ) : null}

        {field.fieldType === TREASURY_FIELD_TYPES.NUMBER ? (
          <TextField
            select
            size="small"
            label="Formato del número"
            value={field.numberFormat}
            onChange={(event) => onChange({ ...field, numberFormat: event.target.value })}
            error={Boolean(errors.numberFormat)}
            helperText={errors.numberFormat}
            sx={roundedInputSx}
            fullWidth
          >
            <MenuItem value="INTEGER">Entero</MenuItem>
            <MenuItem value="DECIMAL">Decimal</MenuItem>
          </TextField>
        ) : null}

        {!selection ? <FieldPreview field={field} /> : null}

        {isMovementCapableField(field.fieldType) ? (
          <TreasuryFormIntegrationEditor
            field={field}
            errors={errors}
            accounts={accounts}
            categories={categories}
            loading={financialLoading}
            loadError={financialError}
            onChange={onChange}
          />
        ) : null}

        {errors.server ? <Alert severity="error">{errors.server}</Alert> : null}
      </Stack>
    </Paper>
  );
}
